import React, { useEffect, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  TableHead,
  TableBody,
  TableCell,
  IconButton,
  Checkbox,
  TableFooter,
  Table,
  TableRow,
  Typography,
  TableSortLabel,
  Box,
  Skeleton,
  TextField,
  FormControlLabel,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useGetStormRates } from 'api/rates';
import { RatesParams } from 'api/rates/rates.types';
import { StyledTextField } from 'components/Inputs';
import { LoadingScreen } from 'components/Loaders/LoadingScreen';
import { CustomTablePagination } from 'components/Table';
import { useAuth } from 'contexts/authContext';
import { useRatesContext } from 'contexts/ratesContext';
import { format } from 'date-fns';
import { t } from 'i18next';
import { debounce } from 'lodash';
import {
  generatePath,
  Navigate,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { RatesTab, reactPaths } from 'root/pathnames';
import { RatesType } from 'root/pathnames/tabs';
import { RELATION_LABEL } from '../RatesForm/RatesForm.consts';
import ActionButtons from './elements/ActionButtons';
import {
  MAIN_HEADERS,
  COLLAPSED_HEADERS,
  PAGINATION_ROWS,
  EXPORT,
  IMPORT,
  ONLY_MINE_LABEL,
  ALL_LABEL,
  PUBLICATION_DATE_FROM_LABEL,
  PUBLICATION_DATE_TO_LABEL,
  STATUS_LABEL,
  PUBLISHED_LABEL,
  DRAFT_LABEL,
  NULL_VALUE,
} from './RatesTable.consts';
import * as S from './RatesTable.css';
import { RowData, SortProperties, Order } from './RatesTable.types';

const debouncedFunc = debounce((fn) => fn(), 500);

const sortPropertyMap: Record<keyof SortProperties, string> = {
  shippingLine: 'shippingLine.counterparty.name',
  startPort: 'startPort.name',
  endPort: 'endPort.name',
  containerType: 'container.containerSize.code',
  rateAllIn: 'currencyAmount.value',
  dateFrom: 'dateFrom',
  dateTo: 'dateTo',
  publicationDate: 'publicationDate',
  rateNumber: 'rateNumber',
};

const reverseSortPropertyMap: Record<string, keyof SortProperties> =
  Object.keys(sortPropertyMap).reduce((acc, key) => {
    const typedKey = key as keyof SortProperties;
    acc[sortPropertyMap[typedKey]] = typedKey;
    return acc;
  }, {} as Record<string, keyof SortProperties>);

export const RatesTable: React.FC = () => {
  const { user } = useAuth();
  const { getTabParams, getSetTabParams } = useRatesContext();
  const params = getTabParams();
  const setParams = getSetTabParams();

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [page, setPage] = useState<number>(params.pageNumber - 1 || 0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    params.resultSize || PAGINATION_ROWS[0],
  );
  const [rows, setRows] = useState<RowData[]>([]);
  const [paginationCount, setPaginationCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [order, setOrder] = useState<Order>(
    (params.sortDirection as Order) || 'desc',
  );
  const [orderBy, setOrderBy] = useState<string>(
    params.sortProperty || 'rateNumber',
  );
  const [searchValue, setSearchValue] = useState<string>(
    params.filterText || '',
  );
  const [orderByFrontend, setOrderByFrontend] = useState<keyof SortProperties>(
    reverseSortPropertyMap[params.sortProperty] || 'rateNumber',
  );
  const [checkedOnlyMine, setCheckedOnlyMine] = useState<boolean>(
    params.onlyMine || false,
  );
  const [filterTradeType, setFilterTradeType] = useState<string>(
    params.tradeType || 'ALL',
  );
  const [filterStatus, setFilterStatus] = useState<string>(
    params.status || 'ALL',
  );
  const [publicationDateFrom, setPublicationDateFrom] = useState<string>(
    params.startPublicationDate
      ? format(new Date(params.startPublicationDate), 'yyyy-MM-dd')
      : '',
  );
  const [publicationDateTo, setPublicationDateTo] = useState<string>(
    params.endPublicationDate
      ? format(new Date(params.endPublicationDate), 'yyyy-MM-dd')
      : '',
  );

  const navigate = useNavigate();
  const { tab: ratesTab } = useParams<{ tab: RatesTab }>();
  const { getStormRates } = useGetStormRates();

  const populateRows = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getStormRates(params);

      const fetchedData = response.data.result.data;
      const fetchedCount = response.data.result.count;

      setPaginationCount(fetchedCount);

      const formattedData = fetchedData.map((item: RowData) => ({
        id: item.id,
        rateNumber: item.rateNumber,
        shippingLine: item?.shippingLine?.counterpartyShortName || '',
        contractNumber: item.contractNumber,
        tradeType: item.tradeType
          ? item.tradeType === 'EXPORT'
            ? EXPORT
            : IMPORT
          : null,
        commodities: item.commodities.map(
          (commodity: { code: string }) => commodity.code,
        ),
        namedAccounts: item.namedAccounts.map(
          (namedAccount) => namedAccount.code,
        ),
        startPort: `${item.startPort.countryCode} ${item.startPort.name}`,
        endPort: `${item.endPort.countryCode} ${item.endPort.name}`,
        stormRateJobs: item.stormRateJobs
          .sort(
            (a, b) =>
              (b.activity.id === 2 ? 1 : 0) - (a.activity.id === 2 ? 1 : 0),
          )
          .map(
            (rateJob) =>
              `${rateJob.activity.code} | ${rateJob.unit.code} | ${rateJob.currencyAmount.value} ${rateJob.currencyAmount.currencyCode.currency}`,
          ),
        containerType: item?.container?.code || '',
        daysOff: item.daysOff,
        dateFrom: item.dateFrom,
        dateTo: item.dateTo,
        rateAllIn: `${item.currencyAmount.value} ${item.currencyAmount.currencyCode.currency}`,
        note: item.note,
        publicationDate: item.publicationDate,
        region: item.region.code,
        relation: item?.tradeType || '',
      }));
      setRows(formattedData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setParams((prev: RatesParams) => ({
      ...prev,
      pageNumber: page + 1,
      resultSize: rowsPerPage,
      sortProperty: orderBy,
      sortDirection: order,
      filterText: searchValue,
      onlyMine: checkedOnlyMine,
      tradeType: filterTradeType !== 'ALL' ? filterTradeType : undefined,
      status: filterStatus !== 'ALL' ? filterStatus : undefined,
      startPublicationDate: publicationDateFrom
        ? new Date(publicationDateFrom).getTime()
        : undefined,
      endPublicationDate: publicationDateTo
        ? new Date(publicationDateTo).getTime()
        : undefined,
    }));
  }, [
    page,
    rowsPerPage,
    orderBy,
    order,
    searchValue,
    checkedOnlyMine,
    filterTradeType,
    filterStatus,
    publicationDateFrom,
    publicationDateTo,
    setParams,
  ]);

  useEffect(() => {
    debouncedFunc(() => {
      populateRows();
    });
  }, [params]);

  const handleRowToggle = (id: number) => {
    const updatedExpandedRows = new Set(expandedRows);
    if (updatedExpandedRows.has(id)) {
      updatedExpandedRows.delete(id);
    } else {
      updatedExpandedRows.add(id);
    }
    setExpandedRows(updatedExpandedRows);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (
    id: number,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
  ) => {
    navigate(
      generatePath(reactPaths.ratesDetails, {
        ratesType: RatesType.RATES,
        tab: ratesTab,
        ratesId: String(id),
      }),
      {
        state: {
          searchValue,
          checkedOnlyMine,
          filterTradeType,
          publicationDateFrom,
          publicationDateTo,
          filterStatus,
          orderBy,
          order,
        },
      },
    );
  };

  const createSortHandler =
    (property: keyof SortProperties) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const isAsc = orderByFrontend === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(sortPropertyMap[property] || property);
      setOrderByFrontend(property);
    };

  const debouncedSearch = (searchTerm: string) => {
    setPage(0);
    debouncedFunc(() => {
      populateRows();
    });
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleOnlyMineCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setCheckedOnlyMine(e.target.checked);
  };

  const handleFilterTradeType = (e: SelectChangeEvent<string>) => {
    setPage(0);
    const selectedTradeType = e.target.value;
    setFilterTradeType(selectedTradeType);
  };

  const handleFilterStatus = (e: SelectChangeEvent<string>) => {
    setPage(0);
    const selectedStatus = e.target.value;
    setFilterStatus(selectedStatus);
  };

  const publicationDateFromHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPage(0);
    const newDateFrom = event.target.value;
    setPublicationDateFrom(newDateFrom);
  };

  const publicationDateToHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPage(0);
    const newDateTo = event.target.value;
    setPublicationDateTo(newDateTo);
  };

  //TODO: Add privilages to remove this clientPanelUser check here
  if (user?.clientPanelUser) {
    return <Navigate to={reactPaths.home} replace />;
  }

  return !rows.length && loading ? (
    <Box>
      <LoadingScreen />
    </Box>
  ) : (
    <S.TableBox>
      <S.TableContainer>
        <ActionButtons
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          refreshTable={() =>
            debouncedFunc(() => {
              populateRows();
            })
          }
        />
        <S.StyledTable>
          {error && (
            <Typography
              color='error'
              align='center'
              sx={{ marginBottom: '20px' }}
            >
              {error}
            </Typography>
          )}

          {!error && (
            <>
              <TableHead>
                <TableRow>
                  <TableCell
                    colSpan={MAIN_HEADERS.length + 2}
                    style={{
                      width: '100%',
                      paddingBottom: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        gap: '0.5rem',
                      }}
                    >
                      <FormControl sx={{ minWidth: 125 }}>
                        <InputLabel>{STATUS_LABEL}</InputLabel>
                        <Select
                          label={STATUS_LABEL}
                          value={filterStatus}
                          size='small'
                          onChange={handleFilterStatus}
                        >
                          <MenuItem value='ALL'>{ALL_LABEL}</MenuItem>
                          <MenuItem value='PUBLISHED'>
                            {PUBLISHED_LABEL}
                          </MenuItem>
                          <MenuItem value='DRAFT'>{DRAFT_LABEL}</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl sx={{ minWidth: 125 }}>
                        <InputLabel>{RELATION_LABEL}</InputLabel>
                        <Select
                          label={RELATION_LABEL}
                          value={filterTradeType}
                          size='small'
                          onChange={handleFilterTradeType}
                        >
                          <MenuItem value='ALL'>{ALL_LABEL}</MenuItem>
                          <MenuItem value='IMPORT'>{IMPORT}</MenuItem>
                          <MenuItem value='EXPORT'>{EXPORT}</MenuItem>
                        </Select>
                      </FormControl>
                      <StyledTextField
                        id='date'
                        size='small'
                        label={PUBLICATION_DATE_FROM_LABEL}
                        type='date'
                        width={170}
                        onChange={publicationDateFromHandler}
                        value={publicationDateFrom}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      <StyledTextField
                        id='date'
                        size='small'
                        label={PUBLICATION_DATE_TO_LABEL}
                        type='date'
                        onChange={publicationDateToHandler}
                        value={publicationDateTo}
                        width={170}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      <TextField
                        placeholder={t('button.search', { ns: 'misc' })}
                        type='text'
                        size='small'
                        sx={{ marginLeft: 'auto' }}
                        value={searchValue}
                        onChange={handleSearch}
                      />
                    </Box>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checkedOnlyMine}
                            onChange={handleOnlyMineCheckbox}
                            name='onlyMineCheckbox'
                            color='primary'
                          />
                        }
                        label={ONLY_MINE_LABEL}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <S.PaginationCell colSpan={MAIN_HEADERS.length + 2}>
                    <CustomTablePagination
                      rowsPerPageOptions={PAGINATION_ROWS}
                      count={paginationCount}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </S.PaginationCell>
                </TableRow>
                <TableRow>
                  <S.StyledTableCell />
                  {MAIN_HEADERS.map(([key, label]) => {
                    const isSortActive = orderByFrontend === key;
                    return (
                      <S.TableHeadCell key={key}>
                        <TableSortLabel
                          active={isSortActive}
                          direction={order}
                          onClick={createSortHandler(
                            key as keyof SortProperties,
                          )}
                        >
                          {label}
                          {isSortActive ? (
                            <Box component='span' sx={visuallyHidden}>
                              {order === 'desc'
                                ? 'sorted descending'
                                : 'sorted ascending'}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      </S.TableHeadCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <S.StyledTableRow
                      expanded={loading ? false : expandedRows.has(row.id)}
                      onClick={(e) => handleRowClick(row.id, e)}
                    >
                      <S.StyledTableCell
                        style={{ width: '3%', textAlign: 'center' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowToggle(row.id);
                        }}
                      >
                        <IconButton sx={{ padding: 0 }}>
                          {expandedRows.has(row.id) ? (
                            !loading ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </S.StyledTableCell>
                      {MAIN_HEADERS.map(([key, _]) => (
                        <S.StyledTableCell key={key}>
                          {!loading ? row[key as keyof RowData] : <Skeleton />}
                        </S.StyledTableCell>
                      ))}
                    </S.StyledTableRow>
                    {expandedRows.has(row.id) && !loading && (
                      <TableRow>
                        <TableCell
                          colSpan={MAIN_HEADERS.length + 2}
                          sx={{ padding: 0, margin: 0 }}
                        >
                          <S.CollapseContentBox
                            in={expandedRows.has(row.id)}
                            timeout='auto'
                            unmountOnExit
                          >
                            <Table>
                              <TableHead>
                                {COLLAPSED_HEADERS.map(([key, label]) => (
                                  <S.CollapseHeader key={key}>
                                    {label}
                                  </S.CollapseHeader>
                                ))}
                              </TableHead>
                              <TableBody>
                                {COLLAPSED_HEADERS.map(([key]) => (
                                  <S.StyledCollapsedTableCell key={key}>
                                    {key === 'namedAccounts' &&
                                    Array.isArray(row[key as keyof RowData]) &&
                                    row[key as keyof RowData].length === 0
                                      ? [NULL_VALUE]
                                      : Array.isArray(row[key as keyof RowData])
                                      ? row[key as keyof RowData].map(
                                          (item: any, index: number) => (
                                            <p
                                              key={index}
                                              style={{
                                                marginBottom:
                                                  index <
                                                  row[key as keyof RowData]
                                                    .length -
                                                    1
                                                    ? '0.5rem'
                                                    : '0',
                                              }}
                                            >
                                              {item}
                                            </p>
                                          ),
                                        )
                                      : row[key as keyof RowData]}
                                  </S.StyledCollapsedTableCell>
                                ))}
                              </TableBody>
                            </Table>
                          </S.CollapseContentBox>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>

              <TableFooter>
                <TableRow>
                  <S.PaginationCell colSpan={MAIN_HEADERS.length + 2}>
                    <CustomTablePagination
                      rowsPerPageOptions={PAGINATION_ROWS}
                      count={paginationCount}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </S.PaginationCell>
                </TableRow>
              </TableFooter>
            </>
          )}
        </S.StyledTable>
        <ActionButtons
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          refreshTable={() =>
            debouncedFunc(() => {
              populateRows();
            })
          }
        />
      </S.TableContainer>
    </S.TableBox>
  );
};
